import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Screen } from '../types';
import { MapContainer, Marker, TileLayer, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

const MapScreen = ({ navigateTo }) => {
  // Fix Leaflet default marker icons in bundlers
  useEffect(() => {
    // eslint-disable-next-line no-underscore-dangle
    delete L.Icon.Default.prototype._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: markerIcon2x,
      iconUrl: markerIcon,
      shadowUrl: markerShadow
    });
  }, []);

  const gates = useMemo(
    () => [
      {
        id: 'bhalukpong',
        name: 'Bhalukpong Gate',
        district: 'West Kameng District',
        kind: 'Entry Gate',
        lat: 26.9935,
        lng: 92.6426,
        hours: '06:00 AM - 08:00 PM',
        waitTime: '~15 Mins',
        status: 'Open'
      },
      {
        id: 'holongi',
        name: 'Holongi',
        district: 'Papum Pare District',
        kind: 'Entry Gate',
        lat: 27.2501,
        lng: 93.6716,
        hours: '06:00 AM - 08:00 PM',
        waitTime: '~10 Mins',
        status: 'Open'
      },
      {
        id: 'gumto',
        name: 'Gumto',
        district: 'Papum Pare District',
        kind: 'Check Post',
        lat: 27.1744,
        lng: 93.6207,
        hours: '24x7',
        waitTime: '~5 Mins',
        status: 'Open'
      }
    ],
    []
  );

  const [activeTab, setActiveTab] = useState('All');
  const [query, setQuery] = useState('');
  const [selectedGateId, setSelectedGateId] = useState(gates[0]?.id);
  const [userLocation, setUserLocation] = useState(null);
  const makeGateIcon = ({ selected, kind }) => {
    const tone = kind === 'Entry Gate' ? 'entry' : kind === 'Check Post' ? 'post' : 'other';
    return L.divIcon({
      className: '',
      html: `
        <div class="ilp-map-marker ilp-map-marker--${tone} ${selected ? 'ilp-map-marker--selected' : ''}">
          <div class="ilp-map-marker__core"></div>
        </div>
      `,
      iconSize: [28, 28],
      iconAnchor: [14, 14]
    });
  };

  const mapRef = useRef(null);

  // Keep the user constrained to India-ish bounds, and start focused on Arunachal / NE India
  const INDIA_MAX_BOUNDS = useMemo(
    () => [
      // Southwest (lat, lng), Northeast (lat, lng)
      [6.0, 67.0],
      [37.5, 99.5]
    ],
    []
  );

  const ARUNACHAL_BOUNDS = useMemo(
    () => [
      [26.4, 91.4],
      [29.4, 97.2]
    ],
    []
  );

  const selectedGate = useMemo(
    () => gates.find((g) => g.id === selectedGateId) || gates[0],
    [gates, selectedGateId]
  );

  const filteredGates = useMemo(() => {
    const q = query.trim().toLowerCase();
    return gates.filter((g) => {
      const matchesTab =
        activeTab === 'All' ||
        (activeTab === 'Entry Gates' && g.kind === 'Entry Gate') ||
        (activeTab === 'Check Posts' && g.kind === 'Check Post') ||
        (activeTab === 'Districts' && g.kind === 'District');

      const matchesQuery =
        !q ||
        g.name.toLowerCase().includes(q) ||
        g.district.toLowerCase().includes(q) ||
        g.kind.toLowerCase().includes(q);

      return matchesTab && matchesQuery;
    });
  }, [activeTab, gates, query]);

  const focusGate = (gate) => {
    if (!gate) return;
    setSelectedGateId(gate.id);
    const map = mapRef.current;
    if (map) {
      map.flyTo([gate.lat, gate.lng], Math.max(map.getZoom(), 9), { duration: 0.6 });
    }
  };

  const handleMyLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setUserLocation(loc);
        const map = mapRef.current;
        if (map) {
          map.flyTo([loc.lat, loc.lng], 10, { duration: 0.7 });
        }
      },
      () => {
        // Ignore errors silently for demo UX
      },
      { enableHighAccuracy: true, timeout: 7000 }
    );
  };

  return (
    <div className="ilp-map-screen">
      {/* Search Header */}
      <div className="ilp-map-header">
        <button className="ilp-map-back-btn" onClick={() => navigateTo(Screen.Home)}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="ilp-map-search">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            placeholder="Search gates or districts"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <button className="ilp-map-menu-btn">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>

      {/* Category Filters */}
      <div className="ilp-map-filters">
        {['All', 'Entry Gates', 'Check Posts', 'Districts'].map((tab) => (
          <button
            key={tab}
            className={`ilp-filter-btn ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Map Background */}
      <div className="ilp-map-background">
        <MapContainer
          center={[28.2, 94.7]}
          zoom={9}
          minZoom={7}
          maxZoom={14}
          maxBounds={INDIA_MAX_BOUNDS}
          maxBoundsViscosity={1.0}
          zoomControl={false}
          className="ilp-leaflet-map"
          whenReady={(ctx) => {
            mapRef.current = ctx.target;
            // On first load, zoom to Arunachal/NE India so only India region is visible
            ctx.target.fitBounds(ARUNACHAL_BOUNDS, { padding: [24, 24] });
          }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {filteredGates.map((g) => (
            <Marker
              key={g.id}
              position={[g.lat, g.lng]}
              icon={makeGateIcon({ selected: g.id === selectedGateId, kind: g.kind })}
              eventHandlers={{
                click: () => focusGate(g)
              }}
            >
              <Tooltip direction="top" offset={[0, -8]} opacity={1} permanent={g.id === selectedGateId}>
                <div className="ilp-leaflet-tooltip">{g.name}</div>
              </Tooltip>
            </Marker>
          ))}

          {userLocation && (
            <Marker
              position={[userLocation.lat, userLocation.lng]}
              icon={L.divIcon({
                className: '',
                html: '<div class="ilp-map-marker ilp-map-marker--me"><div class="ilp-map-marker__core"></div></div>',
                iconSize: [22, 22],
                iconAnchor: [11, 11]
              })}
              eventHandlers={{
                click: () => {
                  const map = mapRef.current;
                  if (map) map.flyTo([userLocation.lat, userLocation.lng], 11, { duration: 0.6 });
                }
              }}
            >
              <Tooltip direction="top" offset={[0, -8]} opacity={1}>
                <div className="ilp-leaflet-tooltip">You</div>
              </Tooltip>
            </Marker>
          )}
        </MapContainer>
      </div>

      {/* Location Button */}
      <button className="ilp-my-location-btn" onClick={handleMyLocation} aria-label="My location">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      </button>

      {/* Info Panel */}
      <div className="ilp-map-panel">
        <div className="ilp-panel-handle"></div>
        <div className="ilp-panel-header">
          <div className="ilp-panel-title">
            <h3 style={{ color: '#fff' }}>{selectedGate?.name || 'Select a location'}</h3>
            <p>{selectedGate?.district || ''}</p>
            <div className="ilp-official-badge">
              <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 4.925-3.467 9.045-8.104 10.04a.75.75 0 01-.192 0C5.166 16.046 1.7 11.926 1.7 7.001c0-.68.056-1.35.166-2.002zm11.76 3.07a.75.75 0 00-1.06-1.06l-3.89 3.89-.935-.935a.75.75 0 00-1.06 1.06l1.465 1.465a.75.75 0 001.06 0l4.42-4.42z" clipRule="evenodd" />
              </svg>
              <span>Official ILP Entry Point</span>
            </div>
          </div>
          <span className="ilp-open-badge">{selectedGate?.status || 'Open'}</span>
        </div>

        <div className="ilp-panel-details">
          <div className="ilp-detail">
            <label>Hours</label>
            <p>{selectedGate?.hours || '06:00 AM - 08:00 PM'}</p>
          </div>
          <div className="ilp-detail">
            <label>Wait Time</label>
            <p>{selectedGate?.waitTime || '~15 Mins'}</p>
          </div>
        </div>

        <div className="ilp-panel-actions">
          <button className="ilp-primary-btn">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" style={{ transform: 'rotate(-45deg)' }}>
              <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
            </svg>
            <span style={{ fontSize: 14 }}>Get Directions</span>
          </button>
          <button className="ilp-panel-icon-btn">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
          </button>
          <button className="ilp-panel-icon-btn">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default MapScreen;

